import { Skeleton } from '@shigo-ui/ui';
import { dashboardT } from '../../../lib/messages';
import { useQuery } from '@tanstack/react-query';
import { useLayoutEffect, useMemo, useState, type ReactNode } from 'react';

import { loadPublicNetworkBrandDetails } from '../../../lib/publicNetworkBranding';
import { readShigoAppConfig } from '../../../lib/config';
import {
  applyRuntimeNetworkBranding,
  clearRuntimeNetworkBranding,
  resolveNetworkBranding,
} from '../../../lib/networkBranding';
import { useShigoSession } from '../../../lib/useShigoSession';
import { NetworkBrandingContext } from '../networkBrandingContext';

export function NetworkBrandingProvider({ children }: { children: ReactNode }) {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  const publicBranding = useQuery({
    queryKey: ['public-network-branding', config.apiBaseUrl, config.networkId],
    queryFn: ({ signal }) => loadPublicNetworkBrandDetails(config, signal),
    enabled: !session && Boolean(config.networkId),
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const loadedBrandDetails = session ? session.user.networkBrandDetails : publicBranding.data;
  const isLoadingPublicBranding = !session && Boolean(config.networkId) && publicBranding.isPending;
  // Signing out clears the query cache, so public branding reloads. Keep the app (and the auth
  // shell's transition) mounted on the last settled branding meanwhile; only the very first load
  // waits behind the skeleton.
  const [settled, setSettled] = useState<{ brandDetails: typeof loadedBrandDetails } | null>(null);
  // Compared through `settled === null` too: settling on "no branding" (undefined) still counts.
  if (
    !isLoadingPublicBranding &&
    (settled === null || settled.brandDetails !== loadedBrandDetails)
  ) {
    setSettled({ brandDetails: loadedBrandDetails });
  }
  const brandDetails = isLoadingPublicBranding ? settled?.brandDetails : loadedBrandDetails;
  const branding = useMemo(
    () =>
      resolveNetworkBranding(brandDetails, {
        displayName: config.displayName,
        logoUrl: config.logoUrl,
        logoLightUrl: config.logoLightUrl,
      }),
    [brandDetails, config.displayName, config.logoLightUrl, config.logoUrl],
  );

  useLayoutEffect(() => {
    applyRuntimeNetworkBranding(document.documentElement, brandDetails);
    return () => {
      clearRuntimeNetworkBranding(document.documentElement);
    };
  }, [brandDetails]);

  if (isLoadingPublicBranding && !settled) {
    return (
      <main
        role="status"
        aria-label={dashboardT('brand.loading')}
        aria-busy="true"
        className="flex min-h-dvh items-center justify-center bg-[var(--bg-page)] px-5 py-10 sm:px-8"
        data-testid="auth-skeleton"
      >
        <div
          aria-hidden="true"
          className="bg-card/90 w-full max-w-[420px] rounded-3xl border border-[var(--border-default)] px-6 py-8 shadow-[0_28px_80px_rgba(0,0,0,0.14)] sm:px-9 sm:py-9"
        >
          <div className="mb-8 flex h-11 items-center justify-center">
            <Skeleton className="h-7 w-32" />
          </div>
          <div className="mb-8 flex flex-col items-center gap-2">
            <Skeleton className="h-9 w-52" />
            <Skeleton className="h-6 w-64 max-w-full" />
          </div>
          <div className="space-y-4">
            <div className="space-y-1">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-11 w-full rounded-2xl" />
            </div>
            <div className="space-y-1">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-11 w-full rounded-2xl" />
            </div>
          </div>
          <Skeleton className="mt-6 h-11 w-full rounded-full" />
        </div>
      </main>
    );
  }

  return (
    <NetworkBrandingContext.Provider value={branding}>{children}</NetworkBrandingContext.Provider>
  );
}
